// ─────────────────────────────────────────────────────────────────────────────
// LifecycleBoard.tsx
// The lifecycle-first dashboard hero. Renders the installed-base opportunity
// across every stage of the solar lifecycle, with maintenance/cleaning as the
// featured (recurring-revenue) card.
//
// Desktop layout is a balanced bento: the featured teal card sits on the LEFT
// and the four stage cards form a 2x2 to its RIGHT (equal height). On mobile
// it stacks: featured card, then the 2x2.
//
// 100% backed by REAL data: reads the `counts` object returned by
// GET /api/leads/segments  →  { installers, cleaning, battery, takeover,
// loanpayoff, longterm }  (already in App state as `segmentCounts`).
//
// Honest empty states: any count that is null/undefined renders as "-"
// (never 0, never a fabricated number).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState } from "react";

type Counts =
  | {
      installers?: number;
      cleaning?: number;
      battery?: number;
      takeover?: number;
      loanpayoff?: number;
      longterm?: number;
    }
  | null
  | undefined;

type SegmentKey =
  | "installers"
  | "cleaning"
  | "battery"
  | "takeover"
  | "loanpayoff"
  | "longterm";

interface Props {
  counts: Counts;
  /** Optional: number of brand-new permits filed today (falls back to the 0-30d bucket). */
  freshToday?: number | null;
  /** Fired when a stage is tapped - wire to your feed (e.g. setView("explore")). */
  onOpenSegment?: (segment: SegmentKey) => void;
}

/* ---------- helpers ---------- */
const num = (v: any): number | null =>
  typeof v === "number" && isFinite(v) ? v : null;

const sumOrNull = (a: number | null, b: number | null): number | null =>
  a == null && b == null ? null : (a || 0) + (b || 0);

function fmtCompact(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 10_000) return Math.round(n / 1000) + "K";
  return n.toLocaleString();
}
const fmtFull = (n: number): string => n.toLocaleString();

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(m.matches);
    const h = () => setReduced(m.matches);
    m.addEventListener ? m.addEventListener("change", h) : m.addListener(h);
    return () => {
      m.removeEventListener ? m.removeEventListener("change", h) : m.removeListener(h);
    };
  }, []);
  return reduced;
}

/** Counts up from its previous value to `value`. Renders "-" when value is null. */
function CountUp({
  value,
  format,
}: {
  value: number | null;
  format: (n: number) => string;
}) {
  const reduced = usePrefersReducedMotion();
  const [disp, setDisp] = useState(0);
  const fromRef = useRef(0);
  const raf = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (value == null) return;
    if (reduced) {
      fromRef.current = value;
      setDisp(value);
      return;
    }
    const from = fromRef.current;
    const to = value;
    const t0 = performance.now();
    const dur = 750;
    const step = (t: number) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      const cur = from + (to - from) * e;
      fromRef.current = cur;
      setDisp(cur);
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [value, reduced]);

  if (value == null) return <>-</>;
  return <>{format(Math.round(disp))}</>;
}

/* ---------- icons ---------- */
const IconDroplet = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2.5c3 4 5.5 6 5.5 9.5a5.5 5.5 0 0 1-11 0C6.5 8.5 9 6.5 12 2.5Z" />
    <path d="M15.5 16.5 18 19M9 19l1.6-1.6" />
  </svg>
);
const IconBolt = (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" /></svg>
);
const IconBattery = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}><rect x="3" y="8" width="14" height="9" rx="2.5" /><path d="M21 11v3" strokeLinecap="round" /></svg>
);
const IconSwap = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M21 12a9 9 0 1 1-2.6-6.3M21 4v5h-5" /></svg>
);
const IconRoof = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round"><path d="m3 11 9-7 9 7" /><path d="M5 10v9h14v-9" /></svg>
);
const IconArrow = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

/* ---------- component ---------- */
export default function LifecycleBoard({ counts, freshToday, onOpenSegment }: Props) {
  const c = counts || {};
  const maintenance = num((c as any).cleaning);
  const newInstalls = num(freshToday) ?? num((c as any).installers);
  const battery = num((c as any).battery);
  const takeover = num((c as any).takeover);
  const mature = sumOrNull(num((c as any).loanpayoff), num((c as any).longterm));

  const open = (s: SegmentKey) => onOpenSegment && onOpenSegment(s);

  const stages: {
    seg: SegmentKey;
    eb: string;
    name: string;
    value: number | null;
    icon: React.ReactNode;
    tone: string; // class suffix
  }[] = [
    { seg: "battery", eb: "Battery upsell", name: "2-5yr systems, primed", value: battery, icon: IconBattery, tone: "blue" },
    { seg: "takeover", eb: "Takeover-ready", name: "Switchable installs", value: takeover, icon: IconSwap, tone: "rose" },
    { seg: "loanpayoff", eb: "Mature systems", name: "10yr+ reroof window", value: mature, icon: IconRoof, tone: "violet" },
    { seg: "installers", eb: "Fresh installs", name: "New permits filed", value: newInstalls, icon: IconBolt, tone: "orange" },
  ];

  return (
    <div className="lcb-root">
      <style>{CSS}</style>

      <div className="lcb-head">
        <div className="lcb-title">Own the <span className="lcb-accent">entire</span> solar market.</div>
        <div className="lcb-sub">Every stage of the installed base, mapped to the door.</div>
      </div>

      <div className="lcb-hero">
        {/* FEATURED - maintenance / cleaning (recurring) */}
        <button className="lcb-feat" onClick={() => open("cleaning")}>
          <div className="lcb-feat-row">
            <span className="lcb-feat-ico">{IconDroplet}</span>
            <span className="lcb-feat-eb">Maintenance &amp; cleaning</span>
            <span className="lcb-feat-mkt">$3B / yr market</span>
          </div>
          <div className="lcb-feat-big">
            <CountUp value={maintenance} format={fmtFull} />
            <span className="lcb-feat-suf">homes in window</span>
          </div>
          <div className="lcb-feat-desc">
            Your installed base is the asset - systems in the cleaning &amp; service window.
            The recurring revenue every other rep ignores while chasing fresh roofs.
          </div>
          <span className="lcb-feat-cta">Work maintenance {IconArrow}</span>
        </button>

        {/* STAGE GRID (2x2, sits beside the feature on desktop) */}
        <div className="lcb-grid">
          {stages.map((s) => (
            <button key={s.seg} className={"lcb-card lcb-card-" + s.tone} onClick={() => open(s.seg)}>
              <div className="lcb-card-top">
                <span className={"lcb-ic lcb-ic-" + s.tone}>{s.icon}</span>
                <span className="lcb-card-eb">{s.eb}</span>
              </div>
              <div className="lcb-card-bot">
                <span className="lcb-card-v">
                  <CountUp value={s.value} format={fmtCompact} />
                </span>
                <span className="lcb-card-nm">{s.name}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- scoped styles (lcb- prefix, no global bleed) ---------- */
const CSS = `
.lcb-root{
  --o:#FF8800; --oW:#FFF2E1; --oD:#FF5500;
  --teal:#0B8A87; --tealW:#E0F4F3; --tealD:#076966;
  --blue:#2970FF; --blueW:#EAF1FF; --blueD:#1B54D9;
  --rose:#F43F7E; --roseW:#FDE7F0; --roseD:#CF2462;
  --violet:#7C5CFC; --violetW:#F1EDFF; --violetD:#5B3FD4;
  --ink:#101114; --line:#ECEEF1; --line2:#E1E4E9;
  font-family:"Inter",system-ui,-apple-system,sans-serif;
  -webkit-font-smoothing:antialiased; margin:0 0 28px; padding:0;
}
.lcb-head{margin:0 0 16px;}
.lcb-title{font-family:"Outfit",sans-serif; font-weight:900; font-size:30px; letter-spacing:-.035em; color:var(--ink); line-height:1.02;}
.lcb-accent{color:var(--o);}
.lcb-sub{font-family:"Outfit",sans-serif; font-weight:600; font-size:14px; color:#262B37; margin-top:6px; letter-spacing:-.01em;}

/* bento: one column on mobile, two columns on desktop */
.lcb-hero{display:grid; grid-template-columns:1fr; gap:13px; align-items:stretch;}
@media (min-width:1024px){ .lcb-hero{grid-template-columns:1.28fr 1fr;} }

.lcb-feat{
  display:flex; flex-direction:column; width:100%; height:100%; text-align:left;
  border:none; cursor:pointer; border-radius:20px; padding:24px; color:#fff;
  position:relative; overflow:hidden;
  background:radial-gradient(130% 130% at 88% -12%, rgba(255,255,255,.16) 0%, transparent 46%), linear-gradient(135deg,#0B8A87 0%,#076966 100%);
  box-shadow:0 14px 34px rgba(8,90,88,.34);
  transition:transform .16s ease, box-shadow .2s ease;
}
.lcb-feat:hover{transform:translateY(-2px); box-shadow:0 18px 40px rgba(8,90,88,.40);}
.lcb-feat:active{transform:translateY(0) scale(.995);}
.lcb-feat-row{display:flex; align-items:center; gap:11px; margin-bottom:16px;}
.lcb-feat-ico{width:38px; height:38px; border-radius:11px; background:rgba(255,255,255,.18); display:flex; align-items:center; justify-content:center; flex:none;}
.lcb-feat-ico svg{width:21px; height:21px; color:#fff;}
.lcb-feat-eb{font-family:"JetBrains Mono",ui-monospace,monospace; font-size:11px; font-weight:700; letter-spacing:.15em; text-transform:uppercase; color:#ffffff;}
.lcb-feat-mkt{margin-left:auto; font-family:"JetBrains Mono",ui-monospace,monospace; font-size:10.5px; font-weight:700; color:#fff; background:rgba(255,255,255,.18); padding:5px 10px; border-radius:999px; white-space:nowrap;}
.lcb-feat-big{font-family:"Outfit",sans-serif; font-weight:900; font-size:46px; letter-spacing:-.04em; line-height:1; display:flex; align-items:baseline; flex-wrap:wrap; color:#fff;}
.lcb-feat-suf{font-size:15px; font-weight:700; color:#ffffff; margin-left:10px; letter-spacing:normal;}
.lcb-feat-desc{font-size:15px; font-weight:500; line-height:1.55; color:#ffffff; margin-top:12px; max-width:52ch;}
.lcb-feat-cta{display:inline-flex; align-items:center; align-self:flex-start; gap:8px; margin-top:auto; background:#fff; color:var(--tealD); font-weight:800; font-size:14px; padding:12px 17px; border-radius:12px;}
.lcb-feat-cta svg{width:16px; height:16px;}

.lcb-grid{display:grid; grid-template-columns:1fr 1fr; gap:11px;}
@media (min-width:1024px){ .lcb-grid{grid-template-rows:1fr 1fr;} }
.lcb-card{
  display:flex; flex-direction:column; justify-content:space-between; min-height:152px; text-align:left; cursor:pointer; background:#fff;
  border:1px solid var(--line); border-radius:16px; padding:18px;
  box-shadow:0 1px 2px rgba(16,17,20,.04),0 2px 6px rgba(16,17,20,.05);
  transition:transform .16s ease, box-shadow .2s ease;
}
.lcb-card:hover{transform:translateY(-2px); box-shadow:0 10px 26px rgba(16,17,20,.10);}
.lcb-card:active{transform:translateY(0) scale(.99);}
.lcb-ic{width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:12px; flex:none;}
.lcb-ic svg{width:18px; height:18px;}
.lcb-ic-orange{background:var(--oW); color:var(--oD);}
.lcb-ic-blue{background:var(--blueW); color:var(--blueD);}
.lcb-ic-rose{background:var(--roseW); color:var(--roseD);}
.lcb-ic-violet{background:var(--violetW); color:var(--violetD);}
.lcb-card-eb{display:block; font-family:"JetBrains Mono",ui-monospace,monospace; font-size:10.5px; font-weight:800; letter-spacing:.1em; text-transform:uppercase; color:var(--ink);}
.lcb-card-v{display:block; font-family:"Outfit",sans-serif; font-weight:900; font-size:30px; letter-spacing:-.035em; color:var(--ink);}
.lcb-card-nm{display:block; font-size:12.5px; color:var(--ink); margin-top:4px; font-weight:600;}

/* colorized stage cards: tinted bg + segment-ink number (Google-style) */
.lcb-card-blue{background:var(--blueW); border-color:#C9DBFF;}
.lcb-card-blue .lcb-card-v{color:var(--blueD);}
.lcb-card-blue .lcb-ic-blue{background:#D7E4FF;}
.lcb-card-rose{background:var(--roseW); border-color:#F8C9DC;}
.lcb-card-rose .lcb-card-v{color:var(--roseD);}
.lcb-card-rose .lcb-ic-rose{background:#F9C0D6;}
.lcb-card-violet{background:var(--violetW); border-color:#DCD2FB;}
.lcb-card-violet .lcb-card-v{color:var(--violetD);}
.lcb-card-violet .lcb-ic-violet{background:#DDD2FC;}
.lcb-card-orange{background:var(--oW); border-color:#FAD9AC;}
.lcb-card-orange .lcb-card-v{color:var(--oD);}
.lcb-card-orange .lcb-ic-orange{background:#FBD29A;}

@media (prefers-reduced-motion:reduce){
  .lcb-feat,.lcb-card{transition:none !important;}
}
`;
